<template>
  <b-sidebar
    :visible="value"
    @hidden="onHidden"
    @change="onSidebarChange"
    right
    shadow
    backdrop
    bg-variant="white"
    sidebar-class="ai-assist-drawer"
    no-header-close
  >
    <template #header>
      <div class="ai-assist-header d-flex align-items-center justify-content-between w-100">
        <div class="d-flex align-items-center">
          <AiIcon :size="16" class="mr-50" aria-label="IA" />
          <span>{{ title }}</span>
        </div>

        <div class="d-flex align-items-center">
          <b-button
            size="sm"
            variant="flat-secondary"
            class="btn-icon"
            :disabled="historyIndex <= 0"
            @click="prevVersion"
            v-b-tooltip.hover.v-secondary
            :title="$t('AI_ASSIST.PrevVersion')"
          >
            <feather-icon icon="ChevronLeftIcon" />
          </b-button>
          <b-button
            size="sm"
            variant="flat-secondary"
            class="btn-icon ml-25"
            :disabled="historyIndex >= history.length - 1"
            @click="nextVersion"
            v-b-tooltip.hover.v-secondary
            :title="$t('AI_ASSIST.NextVersion')"
          >
            <feather-icon icon="ChevronRightIcon" />
          </b-button>

          <button
            type="button"
            class="ai-assist-close ml-50"
            aria-label="Fechar"
            :disabled="loading"
            @click="close"
          >
            &times;
          </button>
        </div>
      </div>
    </template>

    <div class="d-flex flex-column h-100">
      <div class="ai-assist-body flex-grow-1">
        <b-tabs v-model="activeTab" pills card class="ai-assist-tabs">
          <b-tab :title="$t('AI_ASSIST.CreationTab')" :title-link-class="'ai-assist-tab-link'" :disabled="createTabDisabled">
            <div class="ai-assist-meta mb-1">
              <small class="text-muted">{{ $t('AI_ASSIST.Version') }} {{ historyIndex + 1 }} {{ $t('AI_ASSIST.Of') }} {{ history.length }}</small>
            </div>

            <b-alert
              v-if="lastError"
              variant="danger"
              show
              class="mb-1"
            >
              {{ lastError }}
            </b-alert>

            <b-overlay :show="loading" rounded="sm" opacity="0.3">
              <div class="ai-assist-rich-editor" :class="{ 'is-readonly': !canEditCurrentVersion }">
                <send-news-editor
                  ref="aiAssistEditor"
                  :key="editorKey"
                  :instance="editorInstance"
                  :displayContentEditor="true"
                  :currentBody="currentHtml"
                  :isSendNews="true"
                  :isMaximize="false"
                  :userHeaderDisplay="false"
                  :signatureDisplay="false"
                  :userHeaders="[]"
                  :signatures="[]"
                  :contentFooter="''"
                  :readOnly="!canEditCurrentVersion"
                  @maximize="onEditorMaximize"
                  @minimize="onEditorMinimize"
                  @change="onEditorChange"
                  @click.native="onEditorInteract"
                />
              </div>
            </b-overlay>

            <div v-if="currentHistoryPrompt" class="ai-assist-history-prompt mt-1">
              <div class="d-flex align-items-center justify-content-between mb-50">
                <small class="text-muted">{{ $t('AI_ASSIST.PromptUsedInVersion') }}</small>
                <b-button
                  size="sm"
                  variant="flat-secondary"
                  class="d-inline-flex align-items-center"
                  :disabled="loading"
                  @click="copyCurrentHistoryPrompt"
                >
                  <feather-icon icon="CopyIcon" size="14" class="mr-25" />
                  <span>{{ $t('AI_ASSIST.CopyPrompt') }}</span>
                </b-button>
              </div>
              <div class="ai-assist-history-prompt-text">{{ currentHistoryPrompt }}</div>
            </div>
          </b-tab>

          <b-tab :title="$t('AI_ASSIST.SuggestionsTab')" :title-link-class="'ai-assist-tab-link'" :disabled="suggestionTabDisabled">
            <div class="ai-assist-meta mb-1 d-flex justify-content-between align-items-center">
              <small class="text-muted">{{ $t('AI_ASSIST.SuggestionsPortuguese') }}</small>
              <small v-if="lastAnalyzedAt" class="text-muted">{{ $t('AI_ASSIST.Updated') }}: {{ lastAnalyzedAt }}</small>
            </div>

            <b-alert
              v-if="suggestionsError"
              variant="danger"
              show
              class="mb-1"
            >
              {{ suggestionsError }}
            </b-alert>

            <b-overlay :show="suggestionsLoading || applySelectedLoading" rounded="sm" opacity="0.3">
              <div class="ai-assist-suggestions">
                <p v-if="!suggestions.length" class="text-muted mb-0">
                  {{ isCurrentHtmlEmpty ? $t('AI_ASSIST.ClickAnalyzeToGenerateSuggestionsEmptyBody') : $t('AI_ASSIST.ClickAnalyzeToGenerateSuggestions') }}
                </p>
                <div v-else class="ai-assist-tips">
                  <b-card
                    v-for="(tip, idx) in suggestions"
                    :key="idx"
                    no-body
                    class="ai-assist-tip-card"
                  >
                    <b-card-body class="p-75">
                      <div class="ai-assist-tip-head">
                        <div class="d-flex align-items-center">
                          <span class="ai-assist-tip-index">{{ idx + 1 }}</span>
                          <span class="ai-assist-tip-title">{{ $t('AI_ASSIST.Suggestion') }}</span>
                        </div>
                        <b-form-checkbox
                          v-model="selectedSuggestionFlags[idx]"
                          :disabled="suggestionsLoading || applySelectedLoading"
                          switch
                          class="ai-assist-tip-check"
                          :aria-label="$t('AI_ASSIST.SelectSuggestion')"
                        >
                        </b-form-checkbox>
                      </div>
                      <div class="ai-assist-tip-text">{{ tip }}</div>
                    </b-card-body>
                  </b-card>
                </div>
              </div>
            </b-overlay>
          </b-tab>

          <b-tab :title="$t('AI_ASSIST.SuggestionsTagsTab')" :title-link-class="'ai-assist-tab-link'" :disabled="tagSuggestionTabDisabled" v-if="false">

            <div class="mb-1">
              <div v-if="newsTitle"><h3>{{ newsTitle }}</h3></div>
              <div v-if="subjectsName">
                <b-badge
                  v-for="(subject, index) in subjectsName"
                  :key="index + 1"
                  pill
                  class="mr-25"
                  variant="light-primary rounded"
                >
                  {{ subject }}
                </b-badge>
              </div>
              <div v-if="html" class="p-1">{{ html }}</div>
            </div>

            <div class="ai-assist-meta mb-1 d-flex justify-content-between align-items-center">
              <h4 class="">{{ $t('AI_ASSIST.SuggestionsTab') }}</h4>
              <small v-if="lastAnalyzedAt" class="text-muted">{{ $t('AI_ASSIST.Updated') }}: {{ lastAnalyzedAt }}</small>
            </div>

            <div v-if="tagsSuggestions.length > 0">
              <b-form-checkbox
                v-model="selectAllTagsSuggestions"
                switch
                :aria-label="$t('AI_ASSIST.SelectAll')"
              >
              {{ $t('AI_ASSIST.SelectAll') }}
              </b-form-checkbox>
            </div>

            <b-alert
              v-if="tagsSuggestionsError"
              variant="danger"
              show
              class="mb-1"
            >
              {{ tagsSuggestionsError }}
            </b-alert>

            <b-overlay :show="tagsSuggestionsLoading || applyTagsSelectedLoading" rounded="sm" opacity="0.3">
              <div class="ai-assist-suggestions">


                <p v-if="!tagsSuggestions.length" class="text-muted mb-0">
                  {{ $t('AI_ASSIST.ClickAnalyzeToGenerateSuggestions') }}
                </p>
                <b-row v-else>
                  <b-col md="3" v-for="(tip, idx) in tagsSuggestions" :key="idx">
                    <b-card
                      no-body
                      class="ai-assist-tip-card mb-1"
                      @click.stop="selectedTagsSuggestionFlags[idx] = !selectedTagsSuggestionFlags[idx]; selectedTagsSuggestionFlags[idx] === false ? selectAllTagsSuggestions = false : ''"
                    >
                      <b-card-body class="p-75">
                        <div class="ai-assist-tip-head">
                          <div class="d-flex align-items-center">
                            <span class="ai-assist-tip-index">{{ idx + 1 }}</span>
                            <!-- <span class="ai-assist-tip-title">Sugestão</span> -->
                          </div>
                          <b-form-checkbox
                            v-model="selectedTagsSuggestionFlags[idx]"
                            :disabled="tagsSuggestionsLoading || applyTagsSelectedLoading"
                            switch
                            class="ai-assist-tip-check"
                            :aria-label="$t('AI_ASSIST.SelectSuggestion')"
                          >
                          </b-form-checkbox>
                        </div>
                        <div class="ai-assist-tip-text">{{ tip }}</div>
                      </b-card-body>
                    </b-card>
                  </b-col>
                </b-row>
              </div>
            </b-overlay>
          </b-tab>
        </b-tabs>
      </div>

      <div class="ai-assist-footer">
        <div v-if="activeTab === 0">
          <b-form-group :label="$t('AI_ASSIST.Instructions')" label-for="ai-assist-prompt" class="mb-1">
            <b-form-textarea
              id="ai-assist-prompt"
              v-model="prompt"
              rows="3"
              :disabled="loading"
              :placeholder="$t('AI_ASSIST.InstructionPlaceholder')"
            />
          </b-form-group>

          <div class="d-flex justify-content-between">
            <!-- <b-button
              variant="flat-secondary"
              :disabled="loading"
              @click="close"
            >
              Fechar
            </b-button> -->

            <div class="d-flex">
              <b-button
                variant="primary"
                class="mr-50"
                :disabled="loading || !canGenerate"
                @click="generate"
              >
                {{ $t('AI_ASSIST.Generate') }}
              </b-button>

              <b-button
                variant="success"
                :disabled="loading || !currentHtml"
                @click="apply"
              >
                {{ $t('AI_ASSIST.Apply') }}
              </b-button>
            </div>
          </div>
        </div>

        <div v-else>
          <div class="d-flex justify-content-between">
            <!-- <b-button
              variant="flat-secondary"
              :disabled="suggestionsLoading || applySelectedLoading"
              @click="close"
            >
              Fechar
            </b-button> -->

            <div class="d-flex" v-if="activeTab === 1">
              <b-button
                variant="primary"
                class="mr-50"
                :disabled="suggestionsLoading || applySelectedLoading || !canAnalyze"
                @click="analyzeSuggestions"
              >
                {{ $t('AI_ASSIST.Analyze') }}
              </b-button>

              <b-button
                variant="success"
                :disabled="suggestionsLoading || applySelectedLoading || !canApplySelected"
                @click="applySelectedSuggestions"
              >
                {{ $t('AI_ASSIST.ApplySelected') }}
              </b-button>
            </div>

            <div class="d-flex" v-if="activeTab === 2">
              <b-button
                variant="primary"
                class="mr-50"
                :disabled="tagsSuggestionsLoading || applyTagsSelectedLoading || !canSuggestTags"
                @click="analyzeTagsSuggestions"
              >
                {{ $t('AI_ASSIST.AnalyzeTags') }}
              </b-button>

              <b-button
                variant="success"
                :disabled="tagsSuggestionsLoading || applyTagsSelectedLoading || !canApplyTagsSelected"
                @click="applySelectedTagsSuggestions"
              >
                {{ $t('AI_ASSIST.ApplySelected') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BButton,
  BAlert,
  BTabs,
  BTab,
  BCard,
  BCardBody,
  BFormCheckbox,
  BFormGroup,
  BFormTextarea,
  BOverlay,
  VBTooltip,
  BBadge,
  BRow,
  BCol
} from 'bootstrap-vue';

import SendNewsEditor from '../SendNewsEditor.vue';
import AiIcon from '@core/components/icons/AiIcon.vue';
import messagesNotifyHelper from '@/helpers/messages-notify-helper';

export default {
  name: 'AiAssistDrawer',
  directives: {
    'b-tooltip': VBTooltip,
  },
  components: {
    BSidebar,
    BButton,
    BAlert,
    BTabs,
    BTab,
    BCard,
    BCardBody,
    BFormCheckbox,
    BFormGroup,
    BFormTextarea,
    BOverlay,
    SendNewsEditor,
    AiIcon,
    BBadge,
    BRow,
    BCol
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: 'IA',
    },
    html: {
      type: String,
      default: '',
    },
    request: {
      type: Function,
      required: true,
    },
    analyzeRequest: {
      type: Function,
      default: null,
    },
    tagsSuggestionsRequest: {
      type: Function,
      default: null,
    },
    newsTitle: {
      type: String,
      default: ''
    },
    subjectsName: {
      type: Array,
      default: () => []
    },
    target: {
      type: String,
      default: ''
    },
  },
  data() {
    return {
      loading: false,
      prompt: '',
      history: [],
      historyIndex: 0,
      lastError: '',

      editorInstance: '',
      editorKey: 0,

      activeTab: 0,
      suggestionsLoading: false,
      suggestions: [],
      suggestionsError: '',
      lastAnalyzedAt: '',
      analyzedHtml: '',
      selectedSuggestionFlags: {},
      applySelectedLoading: false,

      suppressEditorChange: false,
      suppressEditorInteract: false,

      tagsSuggestionsLoading: false,
      tagsSuggestions: [],
      tagsSuggestionsError: '',
      selectedTagsSuggestionFlags: {},
      applyTagsSelectedLoading: false,
      selectAllTagsSuggestions: false
    };
  },
  computed: {
    createTabDisabled() {
      return !['title', 'summary', 'body'].includes(this.target);
    },
    suggestionTabDisabled() {
      return !['tags', 'title', 'summary', 'body'].includes(this.target);
    },
    tagSuggestionTabDisabled() {
      return !['tags'].includes(this.target);
    },
    currentHtml() {
      return this.history[this.historyIndex]?.html || '';
    },
    canEditCurrentVersion() {
      return this.historyIndex > 0 && !!this.history[this.historyIndex];
    },
    canGenerate() {
      return typeof this.prompt === 'string' && this.prompt.trim().length > 0;
    },
    canAnalyze() {
      // Integra vazia tambem pode ser analisada: as sugestoes vem do contexto (titulo, resumo, chapeu, tags, assuntos)
      return typeof this.analyzeRequest === 'function';
    },
    isCurrentHtmlEmpty() {
      return !this.htmlToText(this.currentHtml);
    },
    canSuggestTags() {
      return typeof this.tagsSuggestionsRequest === 'function' && !!this.currentHtml;
    },
    isLastVersion() {
      return this.historyIndex === this.history.length - 1;
    },
    hasActiveDiffHighlight() {
      const entry = this.history?.[this.historyIndex];
      return !!(this.isLastVersion && Array.isArray(entry?.diff?.changedTexts) && entry.diff.changedTexts.length > 0 && !entry?.diff?.dismissedAt);
    },
    currentHistoryPrompt() {
      const entry = this.history?.[this.historyIndex];
      return typeof entry?.prompt === 'string' ? entry.prompt.trim() : '';
    },
    selectedSuggestions() {
      if (!Array.isArray(this.suggestions) || !this.suggestions.length) return [];
      return this.suggestions
        .map((tip, idx) => ({ tip, idx }))
        .filter(({ idx, tip }) => this.selectedSuggestionFlags[idx] && typeof tip === 'string' && tip.trim().length)
        .map(({ tip }) => tip.trim());
    },
    selectedTagsSuggestions() {
      if (!Array.isArray(this.tagsSuggestions) || !this.tagsSuggestions.length) return [];
      return this.tagsSuggestions
        .map((tip, idx) => ({ tip, idx }))
        .filter(({ idx, tip }) => this.selectedTagsSuggestionFlags[idx] && typeof tip === 'string' && tip.trim().length)
        .map(({ tip }) => tip.trim());
    },
    canApplySelected() {
      return typeof this.request === 'function' && this.selectedSuggestions.length > 0;
    },
    canApplyTagsSelected() {
      return typeof this.tagsSuggestionsRequest === 'function' && !!this.getOriginalHtml() && this.selectedTagsSuggestions.length > 0;
    },
  },
  watch: {
    selectAllTagsSuggestions(val) {
      if (val) {
        this.selectedTagsSuggestionFlags = this.tagsSuggestions.reduce((acc, _t, idx) => {
          acc[idx] = true;
          return acc;
        }, {});
        return;
      }

      const hasTags = Array.isArray(this.tagsSuggestions) && this.tagsSuggestions.length > 0;
      if (!hasTags) return;

      const allCurrentlySelected = this.tagsSuggestions.every((_t, idx) => !!this.selectedTagsSuggestionFlags[idx]);
      if (!allCurrentlySelected) return;

      this.selectedTagsSuggestionFlags = this.tagsSuggestions.reduce((acc, _t, idx) => {
        acc[idx] = false;
        return acc;
      }, {});
    },
    selectedTagsSuggestionFlags: {
      handler() {
        const hasTags = Array.isArray(this.tagsSuggestions) && this.tagsSuggestions.length > 0;
        if (!hasTags) {
          if (this.selectAllTagsSuggestions) this.selectAllTagsSuggestions = false;
          return;
        }

        const allSelected = this.tagsSuggestions.every((_t, idx) => !!this.selectedTagsSuggestionFlags[idx]);
        if (allSelected !== this.selectAllTagsSuggestions) {
          this.selectAllTagsSuggestions = allSelected;
        }
      },
      deep: true,
    },
    value(isOpen) {
      if (isOpen) {
        this.resetHistoryFromCurrentHtml();
        this.resetSuggestions();
        // this.activeTab = 0;
        this.selectCurrentTabTarget();
        this.syncPromptToCurrentVersion();
        this.editorKey += 1;
        this.$nextTick(() => this.syncEditorBodyToCurrentVersion());
      }
    },
    historyIndex() {
      // Persist current edits before switching happens via prev/next.
      // Here we just sync the editor to the newly selected version.
      this.syncPromptToCurrentVersion();
      this.editorKey += 1;
      this.$nextTick(() => this.syncEditorBodyToCurrentVersion());
    },
  },
  created() {
    this.editorInstance = `ai-assist-${this._uid}`;
  },
  methods: {
    selectCurrentTabTarget() {
      switch (this.target) {
        case 'body':
          this.activeTab = 0;
          break;
        case 'tags':
          this.activeTab = 2;
          break;
        default: 
          this.activeTab = 0;
          break;
      }
    },
    onSidebarChange(visible) {
      this.$emit('input', visible);
    },
    onHidden() {
      this.$emit('input', false);
    },
    close() {
      this.$emit('input', false);
    },
    resetHistoryFromCurrentHtml() {
      this.history = [{ html: this.html || '', prompt: '', createdAt: new Date().toISOString() }];
      this.historyIndex = 0;
      this.prompt = '';
      this.lastError = '';
    },
    resetSuggestions() {
      this.suggestions = [];
      this.suggestionsError = '';
      this.lastAnalyzedAt = '';
      this.analyzedHtml = '';
      this.suggestionsLoading = false;
      this.selectedSuggestionFlags = {};
      this.applySelectedLoading = false;
    },
    resetTagsSuggestions() {

    },
    getOriginalHtml() {
      return this.history?.[0]?.html || '';
    },
    htmlToText(html) {
      return String(html || '')
        .replace(/<[^>]*>/g, ' ')
        .replace(/&nbsp;/gi, ' ')
        .trim();
    },
    getCurrentHistoryPrompt() {
      const entry = this.history?.[this.historyIndex];
      return typeof entry?.prompt === 'string' ? entry.prompt : '';
    },
    syncPromptToCurrentVersion() {
      this.prompt = this.getCurrentHistoryPrompt();
    },
    copyCurrentHistoryPrompt() {
      const prompt = this.currentHistoryPrompt;
      if (!prompt || typeof this.$copyText !== 'function') return;

      this.$copyText(prompt)
        .then(() => {
          messagesNotifyHelper.successNotify('IA', this.$t('AI_ASSIST.PromptCopiedSuccessfully'));
        })
        .catch(() => {
          messagesNotifyHelper.errorNotify('IA', this.$t('AI_ASSIST.CouldNotCopyPrompt'));
        });
    },
    prevVersion() {
      if (this.historyIndex > 0) {
        this.persistCurrentEdits();
        this.historyIndex -= 1;
        this.resetSuggestions();
      }
    },
    nextVersion() {
      if (this.historyIndex < this.history.length - 1) {
        this.persistCurrentEdits();
        this.historyIndex += 1;
        this.resetSuggestions();
      }
    },
    getEditorBody() {
      const ref = this.$refs.aiAssistEditor;
      if (!ref) return null;
      // SendNewsEditor exposes `body` as mutable state.
      return typeof ref.body === 'string' ? ref.body : null;
    },
    getEditorQuill() {
      const ref = this.$refs.aiAssistEditor;
      if (!ref) return null;

      // Preferred: SendNewsEditor stores the instance.
      if (ref.quillInstance) return ref.quillInstance;

      // Fallback: reach into the child refs used by SendNewsEditor.
      const inner = ref.$refs?.[`${this.editorInstance}-contentTextarea`];
      if (inner && inner.quill) return inner.quill;

      return null;
    },
    syncEditorBodyToCurrentVersion() {
      const ref = this.$refs.aiAssistEditor;
      if (!ref) return;

      // Avoid dismissing highlight due to programmatic content sync.
      this.suppressEditorChange = true;
      this.suppressEditorInteract = true;
      this.clearDiffHighlightInEditor();

      // When switching versions we want the editor to reflect the selected history item.
      ref.body = this.currentHtml;

      this.$nextTick(() => {
        this.applyDiffHighlightInEditor();
        this.suppressEditorChange = false;
        // Quill may focus itself on mount; wait a tick before allowing dismiss.
        setTimeout(() => {
          this.suppressEditorInteract = false;
        }, 150);
      });
    },
    persistCurrentEdits() {
      if (!this.canEditCurrentVersion) return;
      const editorHtml = this.getEditorBody();
      if (typeof editorHtml !== 'string') return;

      const entry = this.history[this.historyIndex];
      if (!entry) return;

      if (entry.html !== editorHtml) {
        this.$set(this.history, this.historyIndex, {
          ...entry,
          html: editorHtml,
          editedAt: new Date().toISOString(),
        });
      }
    },
    getEffectiveHtml() {
      if (this.canEditCurrentVersion) {
        const editorHtml = this.getEditorBody();
        if (typeof editorHtml === 'string') return editorHtml;
      }
      return this.currentHtml;
    },
    onEditorMaximize() {
      // In the drawer we don't support maximize/minimize; keep editor inline.
    },
    onEditorMinimize() {
      // If the editor ever emits minimize, persist content.
      this.persistCurrentEdits();
    },
    onEditorInteract() {
      // Any intention to interact should dismiss the highlight (reference-only).
      if (this.suppressEditorInteract) return;
      this.dismissDiffHighlight();
    },
    onEditorChange() {
      // If user starts editing, hide highlight.
      if (this.suppressEditorChange) return;
      this.dismissDiffHighlight();
    },
    dismissDiffHighlight() {
      if (!this.hasActiveDiffHighlight) return;
      const entry = this.history?.[this.historyIndex];
      if (!entry || !entry.diff) return;

      this.clearDiffHighlightInEditor();
      this.$set(this.history, this.historyIndex, {
        ...entry,
        diff: {
          ...entry.diff,
          dismissedAt: new Date().toISOString(),
        },
      });
    },
    computeShallowDiffChangedTexts(baseHtml, newHtml) {
      try {
        const base = typeof baseHtml === 'string' ? baseHtml : '';
        const next = typeof newHtml === 'string' ? newHtml : '';
        if (!next || !base) return [];

        const parser = new DOMParser();
        const baseDoc = parser.parseFromString(base, 'text/html');
        const nextDoc = parser.parseFromString(next, 'text/html');

        const blockSelector = 'p,li,h1,h2,h3,h4,h5,h6,blockquote';
        const normalize = (s) => String(s || '')
          .replace(/\s+/g, ' ')
          .trim()
          .toLowerCase()
          .slice(0, 800);

        const baseBlocks = Array.from(baseDoc.body.querySelectorAll(blockSelector));
        const baseSet = new Set(baseBlocks.map(n => normalize(n.textContent)).filter(Boolean));
        if (!baseSet.size) return [];

        const changed = [];
        const nextBlocks = Array.from(nextDoc.body.querySelectorAll(blockSelector));
        nextBlocks.forEach((node) => {
          const sig = normalize(node.textContent);
          if (!sig) return;
          if (!baseSet.has(sig)) {
            const t = String(node.textContent || '').replace(/\s+/g, ' ').trim();
            if (t) changed.push(t.slice(0, 1200));
          }
        });

        // Cap to keep formatting fast.
        return changed.slice(0, 40);
      } catch (_e) {
        return [];
      }
    },
    getHighlightColor() {
      const isDark = typeof document !== 'undefined' && document.body && document.body.classList.contains('dark-layout');
      return isDark ? '#6b5a1a' : '#ffe8a3';
    },
    collapseWhitespaceWithMap(rawText) {
      const raw = String(rawText || '');
      let collapsed = '';
      const map = [];

      let inWs = false;
      for (let i = 0; i < raw.length; i += 1) {
        const ch = raw[i];
        const isWs = ch === ' ' || ch === '\n' || ch === '\t' || ch === '\r' || ch === '\f' || ch === '\v';

        if (isWs) {
          if (!inWs) {
            // represent any whitespace run as a single space
            collapsed += ' ';
            map.push(i);
            inWs = true;
          }
          continue;
        }

        inWs = false;
        collapsed += ch;
        map.push(i);
      }

      return { collapsed: collapsed.trim(), map };
    },
    collapseWhitespace(rawText) {
      return String(rawText || '').replace(/\s+/g, ' ').trim();
    },
    clearDiffHighlightInEditor() {
      const quill = this.getEditorQuill();
      if (!quill) return;
      try {
        quill.formatText(0, quill.getLength(), { background: false }, 'silent');
      } catch (_e) {
        // ignore
      }
    },
    applyDiffHighlightInEditor(attempt = 0) {
      if (!this.hasActiveDiffHighlight) return;
      const entry = this.history?.[this.historyIndex];
      if (!entry?.diff?.changedTexts) return;

      const quill = this.getEditorQuill();
      if (!quill) {
        // Quill init can be async; retry a few times.
        if (attempt < 12) setTimeout(() => this.applyDiffHighlightInEditor(attempt + 1), 60);
        return;
      }

      const text = String(quill.getText() || '');
      const { collapsed: collapsedText, map } = this.collapseWhitespaceWithMap(text);
      const color = this.getHighlightColor();

      // Clear any previous highlight, then apply current one.
      this.clearDiffHighlightInEditor();

      entry.diff.changedTexts.forEach((needleRaw) => {
        const needle = String(needleRaw || '').trim();
        if (!needle) return;

        let idx = text.indexOf(needle);
        if (idx < 0) {
          // Be tolerant to whitespace/newlines differences.
          const collapsedNeedle = this.collapseWhitespace(needle);
          if (collapsedNeedle.length >= 12) {
            const cIdx = collapsedText.indexOf(collapsedNeedle);
            if (cIdx >= 0 && map[cIdx] != null) {
              idx = map[cIdx];
            }
          }
        }

        if (idx < 0) {
          // Last resort: try a shorter prefix.
          const prefix = this.collapseWhitespace(needle).slice(0, 120);
          if (prefix.length >= 12) {
            const cIdx = collapsedText.indexOf(prefix);
            if (cIdx >= 0 && map[cIdx] != null) {
              idx = map[cIdx];
            }
          }
        }

        if (idx < 0) return;

        try {
          const [line, offset] = quill.getLine(idx);
          if (!line) return;
          const lineStart = idx - (offset || 0);
          const lineLen = typeof line.length === 'function' ? line.length() : needle.length;
          const len = Math.max(1, Math.min(lineLen, quill.getLength() - lineStart));
          quill.formatText(lineStart, len, { background: color }, 'silent');
        } catch (_e) {
          // ignore
        }
      });
    },
    async generate() {
      if (this.loading) return;
      if (!this.canGenerate) return;

      this.loading = true;
      this.lastError = '';
      try {
        this.persistCurrentEdits();
        const baseHtml = this.getEffectiveHtml();
        const responseHtml = await this.request({ html: baseHtml, prompt: this.prompt.trim() });

        const nextHtml = typeof responseHtml === 'string' ? responseHtml : '';
        if (!nextHtml) {
          throw new Error(this.$t('AI_ASSIST.AiReturnedEmptyContent'));
        }

        const changedTexts = this.computeShallowDiffChangedTexts(baseHtml, nextHtml);
        this.history = this.history.concat([
          {
            html: nextHtml,
            prompt: this.prompt.trim(),
            createdAt: new Date().toISOString(),
            diff: {
              baseHtml,
              changedTexts,
              dismissedAt: null,
            },
          },
        ]);
        this.historyIndex = this.history.length - 1;
        this.resetSuggestions();

        this.editorKey += 1;
        this.$nextTick(() => this.syncEditorBodyToCurrentVersion());

        // Clear input after generating, per UX request.
        this.prompt = '';
      } catch (error) {
        const message =
          messagesNotifyHelper.getErrorText(error) ||
          (error instanceof Error && !error.isAxiosError && error.message) ||
          this.$t('AI_ASSIST.CouldNotGenerateVersion');

        this.lastError = String(message);
        this.$emit('error', this.lastError, error);
      } finally {
        this.loading = false;
      }
    },
    async analyzeSuggestions() {
      if (this.suggestionsLoading) return;
      if (!this.canAnalyze) return;

      this.suggestionsLoading = true;
      this.suggestionsError = '';
      try {
        this.persistCurrentEdits();
        const html = this.getEffectiveHtml();
        const result = await this.analyzeRequest({ html: this.htmlToText(html) ? html : '' });
        const tips = Array.isArray(result) ? result : [];
        this.suggestions = tips.filter(t => typeof t === 'string' && t.trim().length > 0).map(t => t.trim());
        // As sugestoes sao aplicadas sobre o texto que foi analisado
        this.analyzedHtml = html;
        this.selectedSuggestionFlags = this.suggestions.reduce((acc, _t, idx) => {
          acc[idx] = false;
          return acc;
        }, {});
        this.lastAnalyzedAt = new Date().toLocaleString();

        if (!this.suggestions.length) {
          throw new Error(this.$t('AI_ASSIST.AiDidNotReturnSuggestions'));
        }
      } catch (error) {
        const message =
          messagesNotifyHelper.getErrorText(error) ||
          (error instanceof Error && !error.isAxiosError && error.message) ||
          this.$t('AI_ASSIST.CouldNotAnalyzeText');

        this.suggestionsError = String(message);
        this.$emit('error', this.suggestionsError, error);
      } finally {
        this.suggestionsLoading = false;
      }
    },
    async analyzeTagsSuggestions() {
      if (this.tagsSuggestionsLoading) return;
      if (!this.canSuggestTags) return;

      this.tagsSuggestionsLoading = true;
      this.tagsSuggestionsError = '';
      try {
        this.persistCurrentEdits();
        const body = { html: this.getEffectiveHtml(), title: this.newsTitle, subjectsName: this.subjectsName };
        const result = await this.tagsSuggestionsRequest(body);
        const tips = Array.isArray(result) ? result : [];
        this.tagsSuggestions = tips.filter(t => typeof t === 'string' && t.trim().length > 0).map(t => t.trim());
        this.selectedTagsSuggestionFlags = this.tagsSuggestions.reduce((acc, _t, idx) => {
          acc[idx] = false;
          return acc;
        }, {});
        this.lastAnalyzedAt = new Date().toLocaleString();

        if (!this.tagsSuggestions.length) {
          throw new Error(this.$t('AI_ASSIST.AiDidNotReturnSuggestions'));
        }
      } catch (error) {
        const message =
          messagesNotifyHelper.getErrorText(error) ||
          (error instanceof Error && !error.isAxiosError && error.message) ||
          this.$t('AI_ASSIST.CouldNotAnalyzeText');

        this.tagsSuggestionsError = String(message);
        this.$emit('error', this.tagsSuggestionsError, error);
      } finally {
        this.tagsSuggestionsLoading = false;
      }
    },
    buildApplySelectedPrompt(selectedTips, hasBody = true) {
      const tips = Array.isArray(selectedTips) ? selectedTips : [];
      const numbered = tips.map((t, i) => `${i + 1}. ${t}`).join('\n');
      if (!hasBody) {
        // Sem integra a requisicao vira criacao do texto: as sugestoes sao as instrucoes do que escrever
        return [
          'Escreva a íntegra da notícia seguindo as sugestões abaixo.',
          'Não invente fatos, números ou citações que não estejam no contexto.',
          '',
          'Sugestões selecionadas:',
          numbered,
        ].join('\n');
      }
      return [
        'Você é um editor de texto. Sua tarefa é aplicar as melhorias abaixo ao HTML fornecido.',
        'Regras:',
        '- Retorne APENAS o HTML final (sem explicações, sem markdown).',
        '- Preserve as tags/estrutura HTML existentes; não remova links, listas, quebras e formatações.',
        '- Não invente fatos; apenas melhore clareza, coesão, objetividade e gramática.',
        '',
        'Sugestões selecionadas:',
        numbered,
      ].join('\n');
    },
    async applySelectedSuggestions() {
      if (this.applySelectedLoading) return;
      if (!this.canApplySelected) return;

      this.applySelectedLoading = true;
      this.suggestionsError = '';
      try {
        this.persistCurrentEdits();
        const originalHtml = this.analyzedHtml || this.getOriginalHtml();
        const prompt = this.buildApplySelectedPrompt(this.selectedSuggestions, !!this.htmlToText(originalHtml));

        const responseHtml = await this.request({ html: originalHtml, prompt });
        const nextHtml = typeof responseHtml === 'string' ? responseHtml : '';
        if (!nextHtml) {
          throw new Error(this.$t('AI_ASSIST.AiReturnedEmptyContentApplying'));
        }

        const changedTexts = this.computeShallowDiffChangedTexts(originalHtml, nextHtml);
        this.history = this.history.concat([
          {
            html: nextHtml,
            prompt: `${this.$t('AI_ASSIST.ApplySelected')} (${this.selectedSuggestions.length})`,
            createdAt: new Date().toISOString(),
            diff: {
              baseHtml: originalHtml,
              changedTexts,
              dismissedAt: null,
            },
          },
        ]);
        this.historyIndex = this.history.length - 1;

        // Switch to Creation tab and show the new version.
        this.activeTab = 0;

        // Reset suggestions because they refer to the previous text.
        this.resetSuggestions();

        this.editorKey += 1;
        this.$nextTick(() => this.syncEditorBodyToCurrentVersion());
      } catch (error) {
        const message =
          messagesNotifyHelper.getErrorText(error) ||
          (error instanceof Error && !error.isAxiosError && error.message) ||
          this.$t('AI_ASSIST.CouldNotApplySuggestions');

        this.suggestionsError = String(message);
        this.$emit('error', this.suggestionsError, error);
      } finally {
        this.applySelectedLoading = false;
      }
    },
    applySelectedTagsSuggestions() {
      this.persistCurrentEdits();
      console.log('[applySelectedTagsSuggestions]', this.selectedTagsSuggestions)
      this.$emit('apply-tags', this.selectedTagsSuggestions);
      this.close();
    },
    apply() {
      this.persistCurrentEdits();
      this.dismissDiffHighlight();
      this.$emit('apply', this.getEffectiveHtml());
      this.close();
    },
  },
};
</script>

<style scoped>
/* b-sidebar is rendered via portal; use v-deep so width applies even with scoped CSS */
::v-deep .ai-assist-drawer {
  width: 520px !important;
  max-width: 100vw;
}

@media (min-width: 992px) {
  ::v-deep .ai-assist-drawer {
    width: 800px !important;
  }
}

/* Make the sidebar close button a plain "X" (no square background), including hover */
::v-deep .ai-assist-drawer .b-sidebar-header .close,
::v-deep .ai-assist-drawer .b-sidebar-header .close:hover,
::v-deep .ai-assist-drawer .b-sidebar-header .close:focus,
::v-deep .ai-assist-drawer .b-sidebar-header .close:active {
  background: transparent !important;
  border: 0 !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  outline: none !important;
  padding: 0 !important;
}

::v-deep .ai-assist-drawer .b-sidebar-header .close {
  opacity: 0.8;
  line-height: 1;
}

::v-deep .ai-assist-drawer .b-sidebar-header .close:hover {
  opacity: 1;
}

.ai-assist-close {
  background: transparent;
  border: 0;
  border-radius: 0;
  box-shadow: none;
  color: inherit;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  opacity: 0.85;
  padding: 0;
}

.ai-assist-close:hover,
.ai-assist-close:focus,
.ai-assist-close:active {
  background: transparent;
  box-shadow: none;
  opacity: 1;
  outline: none;
}

.ai-assist-close[disabled] {
  cursor: not-allowed;
  opacity: 0.5;
}

.ai-assist-body {
  padding: 1rem;
  overflow: auto;
}

/* Fix tab content layout (prevent flex-row causing meta to sit beside preview) */
::v-deep .ai-assist-tabs .tab-content {
  width: 100%;
}

::v-deep .ai-assist-tabs .tab-content > .tab-pane {
  width: 100%;
}

/* Keep Bootstrap tab switching working (only active pane visible) */
::v-deep .ai-assist-tabs .tab-content > .tab-pane:not(.active) {
  display: none !important;
}

/* Some global styles may turn tab panes into flex rows; force a normal block layout for the active pane */
::v-deep .ai-assist-tabs .tab-content > .tab-pane.active {
  display: block !important;
}

.ai-assist-meta {
  display: block;
}

.ai-assist-preview {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  padding: 0.75rem;
  min-height: 220px;
  background: #ffffff;
}

.ai-assist-rich-editor {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  overflow: hidden;
  background: #ffffff;
}


.ai-assist-rich-editor.is-readonly {
  opacity: 0.98;
}

.ai-assist-rich-editor.is-readonly ::v-deep .ql-toolbar {
  pointer-events: none;
  opacity: 0.6;
}

.ai-assist-rich-editor.is-readonly ::v-deep .ql-editor {
  cursor: not-allowed;
}

/* Make the Íntegra editor comfortable inside the drawer */
.ai-assist-rich-editor ::v-deep .quill-editor {
  min-height: 260px;
}

.ai-assist-rich-editor ::v-deep .ql-container {
  min-height: 260px;
}

/* Hide controls that don't make sense inside the drawer */
.ai-assist-rich-editor ::v-deep [id^="toolbar-maximize-"] ,
.ai-assist-rich-editor ::v-deep [id^="toolbar-minimize-"] ,
.ai-assist-rich-editor ::v-deep [id^="toolbar-popover-footer-"] ,
.ai-assist-rich-editor ::v-deep [id^="toolbar-acronyms-modal-"] {
  display: none !important;
}

.ai-assist-history-prompt {
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0.5rem;
  background: #ffffff;
  padding: 0.75rem;
}

.ai-assist-history-prompt-text {
  color: rgba(0, 0, 0, 0.75);
  white-space: pre-wrap;
  line-height: 1.35;
}

.ai-assist-tips {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ai-assist-tip-card {
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 0.75rem;
  background: linear-gradient(180deg, #ffffff 0%, #fbfbfd 100%);
  box-shadow: 0 8px 18px rgba(0, 0, 0, 0.06);
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.ai-assist-tip-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 10px 22px rgba(0, 0, 0, 0.08);
}

.ai-assist-tip-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}

.ai-assist-tip-check {
  margin-bottom: 0;
}

/* Switch only (hide the label text, keep the toggle visible) */
.ai-assist-tip-check ::v-deep .custom-control-label {
  font-size: 0;
}

.ai-assist-tip-check ::v-deep .custom-control-label::before,
.ai-assist-tip-check ::v-deep .custom-control-label::after {
  font-size: initial;
}

.ai-assist-tip-index {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 999px;
  background: rgba(115, 103, 240, 0.14);
  color: #7367f0;
  font-weight: 700;
  font-size: 0.8rem;
}

.ai-assist-tip-title {
  font-weight: 700;
  margin-left: 0.5rem;
  color: rgba(0, 0, 0, 0.75);
  letter-spacing: 0.2px;
}

.ai-assist-tip-text {
  color: rgba(0, 0, 0, 0.75);
  line-height: 1.35;
  white-space: pre-wrap;
}

.ai-assist-footer {
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  padding: 1rem;
}

/* Dark layout support */
.dark-layout .ai-assist-preview {
  background: #232a32;
  border-color: #3b4253;
  color: #d0d2d6;
}

/* b-sidebar is rendered via portal; use v-deep so dark mode applies */
::v-deep .dark-layout .ai-assist-drawer {
  background: #283046 !important;
  color: #d0d2d6;
}

::v-deep .dark-layout .ai-assist-drawer .b-sidebar-header,
::v-deep .dark-layout .ai-assist-drawer .b-sidebar-body {
  background: transparent !important;
  color: inherit;
}

::v-deep .dark-layout .ai-assist-drawer .b-sidebar-header {
  border-bottom: 1px solid #3b4253;
}

.dark-layout .ai-assist-rich-editor {
  background: #283046;
  border-color: #3b4253;
}

/* Quill (SendNewsEditor) dark mode inside the drawer */
.dark-layout .ai-assist-rich-editor ::v-deep .ql-toolbar.ql-snow {
  background: #1f2a44;
  border-color: #3b4253;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-container.ql-snow {
  background: #283046;
  border-color: #3b4253;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-editor {
  color: #d0d2d6;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-editor.ql-blank::before {
  color: rgba(208, 210, 214, 0.65);
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-stroke {
  stroke: #d0d2d6;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-fill,
.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-stroke.ql-fill {
  fill: #d0d2d6;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-picker {
  color: #d0d2d6;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-picker-options {
  background: #1f2a44;
  border-color: #3b4253;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-picker-item:hover,
.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-picker-item.ql-selected {
  color: #ffffff;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-tooltip {
  background: #1f2a44;
  border-color: #3b4253;
  color: #d0d2d6;
}

.dark-layout .ai-assist-rich-editor ::v-deep .ql-snow .ql-tooltip input[type="text"] {
  background: #283046;
  border: 1px solid #3b4253;
  color: #d0d2d6;
}

.dark-layout .ai-assist-tip-card {
  border-color: #3b4253;
  background: linear-gradient(180deg, #232a32 0%, #1f252c 100%);
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35);
}

.dark-layout .ai-assist-history-prompt {
  background: #232a32;
  border-color: #3b4253;
}

.dark-layout .ai-assist-history-prompt-text {
  color: #d0d2d6;
}

.dark-layout .ai-assist-tip-index {
  background: rgba(115, 103, 240, 0.18);
  color: #b7b0ff;
}

.dark-layout .ai-assist-tip-title,
.dark-layout .ai-assist-tip-text {
  color: #d0d2d6;
}

.dark-layout .ai-assist-footer {
  border-top-color: #3b4253;
}

</style>
